import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Auth store for managing authentication state.
 * Uses zustand for simple, performant state management.
 * Persists auth token to localStorage for session continuity.
 * 
 * Sprint 31.9 Phase 11: Enhanced for Identity Center session model.
 */

/**
 * Session option available to user after login.
 * Represents a tenant+role combination.
 */
export interface SessionOption {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  role_id: string;
  role_name: string;
  role_slug: string;
  is_primary: boolean;
}

/**
 * Current session context (selected tenant+role).
 */
export interface SessionContext {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  roleId: string;
  roleName: string;
  roleSlug: string;
  isPlatformAdmin: boolean;
  accountType?: string;  // Sprint 34.15: 'individual' or 'organization'
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  permissions: string[];
}

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Sprint 31.9 Phase 11: Session context
  session: SessionContext | null;
  availableSessions: SessionOption[];
  pendingToken: string | null;  // For session selection flow
  requiresSessionSelection: boolean;
  
  // Sprint 41 Phase 3.1: MFA challenge state
  mfaToken: string | null;  // Short-lived JWT for MFA challenge flow
  mfaEnrolled: boolean;  // True if user has confirmed TOTP device
  requiresMfa: boolean;  // True when login requires MFA step
  mfaUserId: string | null;  // User ID from login response (for display during MFA)
  mfaUsername: string | null;  // Username from login response
  
  // Actions
  login: (token: string, user: User, session: SessionContext) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setPermissions: (permissions: string[]) => void;
  /**
   * S49.6: replace the access token in place, keeping the session otherwise intact.
   * Used by silent renewal -- login() would reset user/session state a renewal must preserve.
   */
  setToken: (token: string) => void;
  hasPermission: (permission: string) => boolean;
  
  // Sprint 31.9 Phase 11: Session management actions
  setPendingLogin: (pendingToken: string, options: SessionOption[], userId: string, email: string) => void;
  selectSession: (token: string, session: SessionContext, permissions: string[]) => void;
  switchSession: (token: string, session: SessionContext, permissions: string[]) => void;
  setAvailableSessions: (sessions: SessionOption[]) => void;
  returnToSessionSelect: () => void;  // Go back to session selection without full logout
  
  // Sprint 41 Phase 3.1: MFA actions
  setMfaPending: (mfaToken: string, mfaEnrolled: boolean, userId: string, username: string) => void;
  clearMfaPending: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      session: null,
      availableSessions: [],
      pendingToken: null,
      requiresSessionSelection: false,
      
      // Sprint 41 Phase 3.1: MFA initial state
      mfaToken: null,
      mfaEnrolled: false,
      requiresMfa: false,
      mfaUserId: null,
      mfaUsername: null,
      
      // Login action - called when session is established (single option or after selection)
      login: (token: string, user: User, session: SessionContext) => {
        set({
          token,
          user,
          session,
          isAuthenticated: true,
          isLoading: false,
          pendingToken: null,
          requiresSessionSelection: false,
          // Sprint 41 Phase 3.1: Clear MFA state on login
          mfaToken: null,
          mfaEnrolled: false,
          requiresMfa: false,
          mfaUserId: null,
          mfaUsername: null,
        });
      },
      
      // Logout action
      logout: () => {
        set({
          token: null,
          user: null,
          session: null,
          availableSessions: [],
          pendingToken: null,
          requiresSessionSelection: false,
          isAuthenticated: false,
          isLoading: false,
          // Sprint 41 Phase 3.1: Clear MFA state on logout
          mfaToken: null,
          mfaEnrolled: false,
          requiresMfa: false,
          mfaUserId: null,
          mfaUsername: null,
        });
      },
      
      // Set loading state
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // Update user permissions (called after fetching /me/effective-permissions)
      setToken: (token: string) => {
        set({ token, isAuthenticated: true });
      },

      setPermissions: (permissions: string[]) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, permissions } });
        }
      },
      
      // Check if user has a specific permission
      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;
        return user.permissions.includes(permission);
      },
      
      // Sprint 31.9 Phase 11: Set pending login state (requires session selection)
      setPendingLogin: (pendingToken: string, options: SessionOption[], userId: string, email: string) => {
        set({
          pendingToken,
          availableSessions: options,
          requiresSessionSelection: true,
          isLoading: false,
          // Partial user info while waiting for session selection
          user: { id: userId, email, permissions: [] },
        });
      },
      
      // Sprint 31.9 Phase 11: Complete login after session selection
      selectSession: (token: string, session: SessionContext, permissions: string[]) => {
        const { user } = get();
        set({
          token,
          session,
          isAuthenticated: true,
          pendingToken: null,
          requiresSessionSelection: false,
          user: user ? { ...user, permissions } : null,
        });
      },
      
      // Sprint 31.9 Phase 11: Switch to different session (already authenticated)
      switchSession: (token: string, session: SessionContext, permissions: string[]) => {
        const { user } = get();
        set({
          token,
          session,
          user: user ? { ...user, permissions } : null,
        });
      },
      
      // Sprint 31.9 Phase 11: Update available sessions (for session switcher)
      setAvailableSessions: (sessions: SessionOption[]) => {
        set({ availableSessions: sessions });
      },

      // Sprint 41 Phase 3.1: Set MFA pending state during login flow
      setMfaPending: (mfaToken: string, mfaEnrolled: boolean, userId: string, username: string) => {
        set({
          mfaToken,
          mfaEnrolled,
          requiresMfa: true,
          mfaUserId: userId,
          mfaUsername: username,
          isLoading: false,
        });
      },
      
      // Sprint 41 Phase 3.1: Clear MFA pending state (after challenge or cancel)
      clearMfaPending: () => {
        set({
          mfaToken: null,
          mfaEnrolled: false,
          requiresMfa: false,
          mfaUserId: null,
          mfaUsername: null,
        });
      },
      
      // Sprint 31.11: Return to session selection without full logout
      // Keeps user info and available sessions, clears current session token
      returnToSessionSelect: () => {
        const { user, availableSessions } = get();
        set({
          token: null,
          session: null,
          isAuthenticated: false,
          requiresSessionSelection: true,
          pendingToken: 'return-to-select',  // Marker that we're in re-select mode
          // Preserve user and available sessions
          user,
          availableSessions,
        });
      },
    }),
    {
      name: 'vortex-auth', // localStorage key
      partialize: (state) => ({
        // Only persist token, user, and session - not loading/pending state
        token: state.token,
        user: state.user,
        session: state.session,
        isAuthenticated: state.isAuthenticated,
        availableSessions: state.availableSessions,
      }),
    }
  )
);

// Selector hooks for common auth checks
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useCurrentSession = () => useAuthStore((state) => state.session);
export const useUserPermissions = () => useAuthStore((state) => state.user?.permissions ?? []);
export const useRequiresSessionSelection = () => useAuthStore((state) => state.requiresSessionSelection);
export const useAvailableSessions = () => useAuthStore((state) => state.availableSessions);

// Sprint 41 Phase 3.1: MFA selectors
export const useRequiresMfa = () => useAuthStore((state) => state.requiresMfa);
export const useMfaToken = () => useAuthStore((state) => state.mfaToken);
export const useMfaEnrolled = () => useAuthStore((state) => state.mfaEnrolled);
